import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useEvent } from "shared/lib/useEvent"
import { ProductConfiguratorStepType } from "shared/types/ProductConfiguration"
import { ProductConfiguratorMode } from "shared/types/ProductConfiguratorMode"
import { ButtonProps } from "shared/ui/buttons/Button"

import { useVariantKey } from "entities/products/model/useVariantKey"

import {
  ProductConfiguratorAPI,
  ProductConfiguratorProps
} from "../ProductConfigurator"
import { useProductConfiguratorConfigurationContext } from "./ProductConfiguratorConfigurationContext"
import { useProductConfiguratorContext } from "./ProductConfiguratorContext"

export function useProductConfiguratorViewModel(
  model: ProductConfiguratorProps
) {
  const { t } = useTranslation()
  const { hideTitle, onSubmit } = model

  const context = useProductConfiguratorContext()
  const {
    steps,
    isNextStepDisabled,
    activeStep,
    firstStep,
    activateStep,
    activateNextStep,
    activatePreviousStep,
    resetExternalDimensioning
  } = context

  const { isSubmitting } = useProductConfiguratorConfigurationContext()

  const { productType } = useVariantKey(model.variantKey)
  const className = classNames("product-configurator", model.className)

  const primaryButtonProps = useMemo<ButtonProps>(() => {
    const isFinalStep = activeStep === ProductConfiguratorStepType.SUMMARY

    return {
      onClick: isFinalStep ? onSubmit : activateNextStep,
      label: isFinalStep
        ? t("Product.Configuration.Submit")
        : t("General.Next"),
      disabled: isNextStepDisabled,
      isLoading: isFinalStep ? isSubmitting : undefined
    }
  }, [
    onSubmit,
    isSubmitting,
    activeStep,
    activateNextStep,
    isNextStepDisabled,
    t
  ])

  const secondaryButtonProps = useMemo<ButtonProps>(() => {
    const isFirstStep = activeStep === firstStep

    return {
      onClick: activatePreviousStep,
      disabled: isFirstStep,
      label: t("General.Previous"),
      isHidden: isFirstStep
    }
  }, [activeStep, activatePreviousStep, firstStep, t])

  // The external calculator owns the full height while it is open, and the only
  // live footer control is disabled until a dimensioning result arrives. Once it
  // has, the footer is needed again to leave the step.
  const hideFooter =
    activeStep === ProductConfiguratorStepType.DIMENSIONING &&
    !model.externalProductDimensioningModel?.externalDimensioningResult

  const handleCompleteExternalDimensioning = useEvent(() => {
    if (activeStep === ProductConfiguratorStepType.DIMENSIONING) {
      activateNextStep()
    }
  })

  const onAdjustDimensioning = useEvent(() => {
    activateStep(ProductConfiguratorStepType.DIMENSIONING)
    resetExternalDimensioning()
  })

  const productConfiguratorAPI = useMemo<ProductConfiguratorAPI>(
    () => ({
      onCompleteExternalProductDimensioning: handleCompleteExternalDimensioning
    }),
    [handleCompleteExternalDimensioning]
  )
  const { apiRef } = model

  useEffect(() => {
    if (apiRef) {
      apiRef.current = productConfiguratorAPI
    }
  }, [productConfiguratorAPI])

  return {
    className,
    hideTitle,
    productType,
    mode: model.mode || ProductConfiguratorMode.CONFIGURATION,
    steps,
    activeStep,
    hideSteps: steps.length <= 1,
    hideFooter,
    primaryButtonProps,
    secondaryButtonProps,
    onAdjustDimensioning,

    externalProductDimensioningModel: model.externalProductDimensioningModel,
    externalDimensioningBaseProductCardComponent:
      model.externalDimensioningBaseProductCardComponent,
    addonOptions: model.addonOptions,
    onAddonDelete: model.onAddonDelete,

    productConfigurationResponse: model.productConfigurationResponse,
    onDimensioningSelect: model.onDimensioningSelect
  }
}
